'use client'

import {
  FieldError as FieldErrorPrimitive,
  type FieldErrorProps,
} from 'react-aria-components/FieldError'
import { LabelContext, Label as LabelPrimitive, type LabelProps } from 'react-aria-components/Label'
import { Text, type TextProps } from 'react-aria-components/Text'
import { cn } from 'cn'
import { tv } from 'tailwind-variants'
import { cx } from '@/lib/primitive.ts'

export const labelStyles = tv({
  base: [
    "select-none text-base/6 text-fg in-data-required:not-data-[slot='control-label']:after:ml-1.5 sm:text-sm/6",
    "in-data-required:not-data-[slot='control-label']:after:text-danger-subtle-fg in-data-required:not-data-[slot='control-label']:after:content-['*']",
    'in-disabled:pointer-events-none in-disabled:opacity-50 group-disabled:opacity-50',
  ],
})

export const descriptionStyles = tv({
  base: 'block text-muted-fg text-sm/6 in-disabled:opacity-50 group-disabled:opacity-50',
})

export const fieldErrorStyles = tv({
  base: 'block text-danger-subtle-fg text-sm/6 in-disabled:opacity-50 group-disabled:opacity-50 forced-colors:text-[Mark]',
})

export const fieldStyles = tv({
  base: [
    'w-full',
    '[&>[data-slot=control]+[data-slot=control]]:mt-2',
    '[&>[data-slot=label]+[data-slot=control]]:mt-2',
    "[&>[data-slot=label]+[slot='description']]:mt-1",
    '[&>[slot=description]+[data-slot=control]]:mt-2',
    '[&>[data-slot=control]+[slot=description]]:mt-2',
    '[&>[data-slot=control]+[slot=errorMessage]]:mt-2',
    '*:data-[slot=label]:font-medium',
    'in-disabled:opacity-50 disabled:opacity-50',
  ],
})

export function Label({ className, htmlFor, slot, ...props }: LabelProps) {
  const label = (
    <LabelPrimitive
      data-slot="label"
      htmlFor={htmlFor}
      slot={slot}
      {...props}
      className={labelStyles({ className })}
    />
  )

  if (htmlFor && slot === undefined) {
    return <LabelContext.Provider value={null}>{label}</LabelContext.Provider>
  }

  return label
}

export function Description({ className, ...props }: TextProps) {
  return <Text {...props} slot="description" className={descriptionStyles({ className })} />
}

export function Fieldset({ className, ...props }: React.ComponentProps<'fieldset'>) {
  return (
    <fieldset
      className={cn('*:data-[slot=text]:mt-1 [&>*+[data-slot=control]]:mt-6', className)}
      {...props}
    />
  )
}

export function FieldGroup({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return <div data-slot="control" className={cn('space-y-6', className)} {...props} />
}

export function FieldError({ className, ...props }: FieldErrorProps) {
  return <FieldErrorPrimitive {...props} className={cx(fieldErrorStyles(), className)} />
}

export function Legend({ className, ...props }: React.ComponentProps<'legend'>) {
  return (
    <legend
      data-slot="legend"
      {...props}
      className={cn('font-semibold text-base/6 data-disabled:opacity-50', className)}
    />
  )
}
